import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import Head from 'next/head';
import {NextPageContext} from 'next';
import getConfig from 'next/config';
import styled, {css} from 'styled-components';
import {rem, rgba} from 'polished';

import Layout from '~/src/components/layout/Layout';
import Ad from '~/src/components/ad/Ad';
import TeaserSectionUI from '~/src/components/teaser/section/TeaserSection';
import NoContent from '~/src/components/category/noContent/NoContent';
import StructuredBreadcrumbsCategory from '~/src/components/structured/breadcrumbs/StructuredBreadcrumbsCategory';
import Breadcrumb from '~/src/components/breadcrumb/Breadcrumb';
import TeaserSectionPlaceholder from '~/src/components/teaser/section/placeholder/TeaserSectionPlaceholder';
import InfinityTrigger from '~/src/components/infinity/Infinity';

import {fetchCategoryData} from '~/src/utilities/feed/category';
import {updateTotmOnClient} from '~/src/utilities/libs/totm';
import {ResponseError} from '~/src/utilities/types/error';
import {getCategoryBy, getCategoryList} from '~/src/utilities/feed/sitemap';
import {SiteArea, SiteAreaName} from '~/src/utilities/constants/navigation';
import {getAdUnitName} from '~/src/utilities/helpers/ad';
import {setCacheControl} from '~/src/utilities/helpers/header';
import {SectionTypes} from '~/src/utilities/constants/types';
import {AdFormat} from '~/src/utilities/constants/formats';
import {GtmEvents, GtmPageTypes, BeagleEvents} from '~/src/utilities/constants/tracking';
import {store, getCached} from '~/src/utilities/helpers/cache';
import {CacheNamespace} from '~/src/utilities/constants/cache';
import {SitemapContext} from '~/src/components/context/SitemapContext';
import {HistoryContext} from '~/src/components/context/HistoryContext';
import {NavigationStateContext} from '~/src/components/context/NavigationStateContext';
import {createAbsoluteUrl, getCategoryUrl, getRootPath} from '~/src/utilities/helpers/link';
import initPolyglot from '~/src/utilities/libs/polyglot';
import PageContext from '~/src/components/context/PageContext';
import {sidelineStack} from '~/src/utilities/helpers/sidelineStack';
import {SitemapEntryCategory} from '~/src/types/data/sitemap';
import {CategoryPageContent} from '~/src/types/data/category';
import {FeedCategoryId} from '~/src/types/feed/common';
import {TeaserSection} from '~/src/types/data/teaser';

import variables from '~/src/css/global.module.scss';
import i18n from './CategoryPage.i18n.json';

const getTranslation = initPolyglot(i18n);

const {serverRuntimeConfig, publicRuntimeConfig: config} = getConfig();

/**
 * Types & Interfaces
 */
interface CustomPage<P, IP> extends React.FC<P> {
    getInitialProps: (ctx: Partial<NextPageContext>, props?: InitialPageProps) => Promise<IP>;
}

interface CategoryPageProps {
    category: SitemapEntryCategory;
    content: CategoryPageContent;
    page?: number;
    pageCount: number;
}

interface InitialPageProps {
    sitemap?: any;
}

interface Navigation {
    categoryId: FeedCategoryId;
    showSubCategories: boolean;
}

interface InitialPageResult {
    data: CategoryPageProps;
    navigation: Navigation;
    siteArea: typeof SiteArea[keyof typeof SiteArea];
}

interface PageState {
    sections: TeaserSection[];
    page: number;
    pageCount: number;
    nextPage?: Function;
}

/**
 * Styling
 */
const Page = styled.div`
    background-color: ${rgba(variables.mainBackgroundColor, 0.75)};
    position: relative;

    @media only screen and (min-width: ${variables.breakpointTablet}) {
        margin: 0 ${rem(-10, 16)};
        padding: 0 ${rem(10, 16)};
    }
    @media only screen and (min-width: ${variables.breakpointFull}) {
        margin: 0 ${rem(-20, 16)};
        padding: 0 ${rem(20, 16)};
    }
`;

const InsideFullBrandDay = styled.div`
    padding-bottom: ${rem(10, 16)};

    @media only screen and (min-width: ${variables.breakpointTablet}) {
        margin: 0 ${rem(-10, 16)};
    }
    @media only screen and (min-width: ${variables.breakpointFull}) {
        margin: 0 ${rem(-20, 16)};
        padding-bottom: ${rem(20, 16)};
    }
`;

const OutsideRight = styled.div<{$navigationCollapsed?: boolean}>`
    display: none;
    height: 100%;
    position: absolute;
    top: 0;
    transform: translateY(0);
    transition: transform 150ms ease-in-out;

    ${({$navigationCollapsed}) =>
        $navigationCollapsed &&
        css`
            transform: translateY(${rem(-50, 16)});

            @media only screen and (min-width: ${variables.breakpointTablet}) {
                transform: translateY(${rem(-73, 16)});
            }

            @media only screen and (min-width: ${variables.breakpointDesktop}) {
                transform: translateY(${rem(-71, 16)});
            }
        `}

    @media only screen and (min-width: ${variables.breakpointTablet}) {
        left: 100%;
    }

    @media only screen and (min-width: ${variables.breakpointTabletAdSmall}) {
        display: block;
    }

    @media only screen and (min-width: ${variables.breakpointTabletAdLarge}) {
        display: block;
    }
`;

const Pager = styled.a`
    background-color: ${variables.accentColor};
    border-radius: ${rem(4, 16)};
    color: ${variables.colorWhite};
    display: block;
    font-size: ${rem(16, 16)};
    height: ${rem(32, 16)};
    line-height: ${rem(32, 16)};
    margin: ${rem(32, 16)} auto;
    text-align: center;
    width: 100%;

    @media only screen and (min-width: ${variables.breakpointTablet}) {
        height: ${rem(40, 16)};
        line-height: ${rem(40, 16)};
        padding: 0 ${rem(52, 16)};
        max-width: ${rem(320, 16)};
    }
`;

/**
 * Helpers
 */
const getData = async (url: string, sitemap: any) => {
    const data = await fetchCategoryData(url, {siteArea: SiteArea.NEWS, sitemap});

    if ('content' in data) {
        store(url, data, CacheNamespace.CATEGORY, 300);

        return data;
    }
};

const splitIntoPages = (sections: TeaserSection[]) => {
    const filtered = sections.filter((element) => SectionTypes.includes(element.subtype) && element.elements?.length);
    const sizes = filtered.map(({elements}) => Math.max(5, elements.length));

    return sizes
        .reduce<number[]>((positions, size, index) => {
            const start = positions[positions.length - 1] || 0;

            // count teasers since last past position
            const sum = sizes.slice(start, index).reduce((acc, n) => acc + n, 0) + size;

            // allow a maximum of 25 teasers
            if (!positions.length || sum > 25) {
                positions.push(index);
            }

            return positions;
        }, [])
        .map((position, index, positions) => filtered.slice(position, positions[index + 1] || filtered.length));
};

const pageCache: Record<string, number> = {};
const usePagination = (sections: TeaserSection[], page: number, pageCount: number, url: string): PageState => {
    if (page > 1) {
        return {
            sections,
            page,
            pageCount
        };
    }

    const pages = useMemo(() => splitIntoPages(sections), [sections]);

    const {isPoppedState} = useContext(HistoryContext);
    const [state, setState] = useState(isPoppedState ? pageCache[url] : page);

    const nextPage = useCallback(() => {
        setState(state + 1);
    }, [state]);

    process.browser &&
        useEffect(() => {
            pageCache[url] = state;
        }, [state]);

    return {
        sections: pages.slice(0, state).reduce((acc, item) => [...acc, ...item], []),
        page: state,
        pageCount: pages.length > 1 ? pages.length : pageCount,
        nextPage
    };
};

const useOnce = (once: Function) => {
    const willRun = useRef(true);

    if (willRun.current) {
        once();

        willRun.current = false;
    }
};

const CategoryPage: CustomPage<CategoryPageProps, InitialPageResult> = (props) => {
    const {isFront, all} = useContext(SitemapContext);
    const {isCollapsed: navigationCollapsed} = useContext(NavigationStateContext);

    const {category, content} = props;
    const {name, metaTitle, metaDescription, fullUrlPath, feed, categoryId, allowRobots = false} = category;
    const gtmPageType = isFront(category) ? GtmPageTypes.FRONT : GtmPageTypes.CHANNEL;
    const isPageOne = !props.page || props.page === 1;

    useOnce(() => {
        updateTotmOnClient({
            adsEnabled: true,
            adChannel: fullUrlPath
        });
    });

    const [state, setState] = useState({
        isLoading: process.browser && !content?.elements?.length,
        content: {...content} as CategoryPageContent
    });

    const {sections, page, pageCount, nextPage} = usePagination(
        state.content.elements || [],
        props.page || 1,
        props.pageCount || 0,
        props.category.fullUrlPath
    );

    if (process.browser) {
        useEffect(() => {
            isPageOne &&
                getData(feed, all).then((data) => {
                    if (data) {
                        setState({
                            isLoading: false,
                            content: data.content as CategoryPageContent
                        });
                    }
                });

            import('~/src/utilities/tracking/beagle').then(({trackRouteChange, trackEvent}) => {
                trackRouteChange();
                trackEvent(BeagleEvents.OPEN_CATEGORY, {category: fullUrlPath});
            });

            import('~/src/utilities/tracking/gtm').then(({trackGtm, buildNavStatusLevels}) => {
                trackGtm(GtmEvents.PAGEVIEW, {
                    pageType: gtmPageType,
                    data: {
                        ...props,
                        ...buildNavStatusLevels(SiteAreaName[SiteArea.NEWS], categoryId.toString(), all),
                        canonicalUrl
                    }
                });
            });
        }, []);
    }

    const isFrontpage = isFront(category);
    const title = metaTitle || getTranslation('title', {name: config.tenantName, category: name});
    const path = getCategoryUrl(fullUrlPath);
    const canonicalUrl = useMemo(() => createAbsoluteUrl(page > 1 ? `${getCategoryUrl(fullUrlPath)}?page=${page}` : getCategoryUrl(fullUrlPath)), [page]);
    const getNextSideline = useMemo(sidelineStack, []);
    const relPrevUrl = page > 2 ? `${path}?page=${page - 1}` : path;
    const relNextUrl = `${path}?page=${page + 1}`;
    const pageContext = {canonicalUrl, gtmPageType};

    return (
        <PageContext.Provider value={pageContext}>
            <Layout>
                <Head>
                    <title>{title}</title>
                    <link key="canonical" rel="canonical" href={canonicalUrl} />

                    {isFrontpage && (
                        <React.Fragment>
                            {config.languages.map((language: string) => (
                                <link
                                    rel="alternate"
                                    hrefLang={language}
                                    href={`${config.canonicalBaseUrl}${getRootPath(language) !== '/' ? getRootPath(language) : ''}`}
                                    key={language}
                                />
                            ))}
                        </React.Fragment>
                    )}

                    {!allowRobots && <meta key="robots" name="robots" content="noindex, follow" />}
                    {metaDescription && <meta key="description" name="description" content={metaDescription} />}
                </Head>

                <StructuredBreadcrumbsCategory categoryId={category.categoryId} />

                <InsideFullBrandDay>
                    <Ad adUnitName={getAdUnitName('inside-full-brandday')} format={AdFormat.INSIDE_FULL_BRANDDAY} />
                </InsideFullBrandDay>

                <Page>
                    {!isFrontpage && <Breadcrumb mainCategoryId={state.content.category} />}

                    <OutsideRight $navigationCollapsed={navigationCollapsed}>
                        <Ad adUnitName={getAdUnitName('outside-right')} format={AdFormat.OUTSIDE_RIGHT} />
                    </OutsideRight>

                    {!isPageOne && (
                        <React.Fragment>
                            <Pager href={relPrevUrl} rel="prev">
                                {getTranslation('pageX', {page: page - 1})}
                            </Pager>
                        </React.Fragment>
                    )}

                    {state.isLoading ? (
                        <TeaserSectionPlaceholder />
                    ) : sections && sections.length > 0 ? (
                        sections.map((element, index) => <TeaserSectionUI key={index} {...element} getNextSideline={getNextSideline} />)
                    ) : (
                        <NoContent />
                    )}

                    {page < pageCount && (
                        <React.Fragment>
                            {isPageOne && <InfinityTrigger loadFunction={nextPage} scrollOffset={process.browser ? document.body.scrollHeight * 0.33 : 0} />}
                            <Pager href={relNextUrl} rel="next">
                                {getTranslation('pageX', {page: page + 1})}
                            </Pager>
                        </React.Fragment>
                    )}
                </Page>
            </Layout>
        </PageContext.Provider>
    );
};

CategoryPage.getInitialProps = async ({query, res}, props) => {
    const pathname = query?.pathname && query.pathname !== 'undefined' ? query.pathname : config.frontCategory;
    const sitemap = getCategoryList(props?.sitemap);
    const category = getCategoryBy(sitemap, {fullUrlPath: pathname});
    if (!category || query?.pathname === config.frontCategory) {
        const statusCode = 404;
        const statusText = 'Category not found';
        return {
            error: new ResponseError({statusCode, statusText})
        };
    }

    const navigation = {
        categoryId: category.categoryId,
        showSubCategories: true
    };

    const categoryData = process.browser
        ? getCached(category.feed, CacheNamespace.CATEGORY) || {}
        : await fetchCategoryData(category.feed, {siteArea: SiteArea.NEWS, sitemap});

    if (categoryData.error) {
        return categoryData;
    }

    const pages = splitIntoPages(categoryData.content?.elements || []);
    const page = Math.max(1, Math.min(pages.length, query?.page ? parseInt(query.page.toString(), 10) : 1));

    const data = {
        ...categoryData,
        category,
        page,
        pageCount: pages.length,
        content: {
            ...categoryData.content,
            elements: process.browser ? categoryData.content?.elements || [] : pages[page - 1]
        }
    };

    if (serverRuntimeConfig.pageCaching) {
        setCacheControl(res, 30, categoryData.date);
    }

    const siteArea = SiteArea.NEWS;

    return {
        data,
        navigation,
        siteArea
    };
};

export default CategoryPage;
